import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const ProfilePage = () => {
  const { profile, updateProfile, updatePassword, loading: authLoading } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    proxyPassword: '',
    newPassword: '',
    referralCode: ''
  });

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        address: profile.address || '',
        proxyPassword: profile.proxy_password || '',
        newPassword: '',
        referralCode: profile.referral_code || ''
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const profileUpdates = {
        name: formData.name,
        address: formData.address,
        proxy_password: formData.proxyPassword,
      };

      const result = await updateProfile(profileUpdates);

      if (!result.success) {
        throw new Error(result.error);
      }

      if (formData.newPassword) {
        if (formData.newPassword.length < 8) {
          throw new Error('Password must be at least 8 characters long');
        }
        if (!/[A-Z]/.test(formData.newPassword) || !/[a-z]/.test(formData.newPassword) || !/[0-9]/.test(formData.newPassword)) {
          throw new Error('Password must contain uppercase, lowercase, and a number');
        }

        const passwordResult = await updatePassword(formData.newPassword);

        if (!passwordResult.success) {
          throw new Error(passwordResult.error);
        }

        setFormData(prev => ({ ...prev, newPassword: '' }));
      }

      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        address: profile.address || '',
        proxyPassword: profile.proxy_password || '',
        newPassword: '',
        referralCode: profile.referral_code || ''
      });
    }
    setError('');
    setSuccess('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  return (
    <div className="profile-page">
      <h1 className="page-title profile-title">Profile</h1>

      {success && (
        <div className="success-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22,4 12,14.01 9,11.01"/>
          </svg>
          {success}
        </div>
      )}

      {error && (
        <div className="error-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-group">
          <label>Name<span className="required">*</span></label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your name"
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label>Email address<span className="required">*</span></label>
          <input
            type="email"
            name="email"
            value={formData.email}
            disabled
            className="disabled-input"
          />
          <span className="field-hint">Email cannot be changed</span>
        </div>

        <div className="form-group address-section">
          <div className="address-header">
            <span>Address Information</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6,9 12,15 18,9"/>
            </svg>
          </div>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Enter your address details (optional)"
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label>Proxy Password</label>
          <div className="input-with-copy">
            <input
              type="text"
              name="proxyPassword"
              value={formData.proxyPassword}
              onChange={handleChange}
              disabled={saving}
            />
            <button
              type="button"
              className="copy-btn"
              onClick={() => copyToClipboard(formData.proxyPassword)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
          <span className="field-hint">This password is used for proxy authentication</span>
        </div>

        <div className="form-group">
          <label>New password</label>
          <input
            type="password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            placeholder="Enter new password (min 8 characters)"
            disabled={saving}
          />
          <span className="field-hint">Leave empty to keep current password</span>
        </div>

        <div className="form-group">
          <label>Referral code</label>
          <div className="input-with-copy">
            <input
              type="text"
              name="referralCode"
              value={formData.referralCode}
              disabled
              className="disabled-input"
            />
            <button
              type="button"
              className="copy-btn"
              onClick={() => copyToClipboard(formData.referralCode)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
          <span className="field-hint">Share this code to refer friends</span>
        </div>

        {profile?.referred_by && (
          <div className="referral-status-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span>Referred — bonus bandwidth may be applied after verification</span>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="save-btn"
            disabled={saving || authLoading}
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
          <button
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>

      <div className="two-factor-section">
        <h3>Two-factor authentication (2FA)</h3>

        <div className="auth-method">
          <div className="auth-info">
            <h4>Authenticator app</h4>
            <div className="setup-link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <span>Set up</span>
            </div>
            <p>Use a secure app to generate a temporary code for login verification.</p>
          </div>
          <span className={`auth-status ${profile?.two_factor_enabled ? 'enabled' : 'disabled'}`}>
            {profile?.two_factor_enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
